/**
 * Desktop notification/error push stream over the daemon's loopback `/events`
 * relay — the second transport under the one renderer demux
 * (events ADR-012). The renderer never opens a cloud bridge
 * socket for this device (bridge ADR-001/004).
 */

import type {
  ErrorEventFrame,
  NotificationCursor,
  NotificationEventFrame,
} from '../types/notification-types.js';
import type { FeedPost } from '../types/feed.js';
import type { SupportEvent } from '../types/support.js';
import {
  SEEN_PUSH_EVENT_CAP,
  comparePushKey,
  type ErrorEventHandler,
  type FeedEventHandler,
  type FeedRegistrar,
  type NotificationCursorStore,
  type NotificationEventHandler,
  type NotificationRegistrar,
  type SupportEventHandler,
} from './bridge-events.js';
import type { ShogunChopSharedEvents } from './shogunchop.js';
import {
  CatalogHolderRegistry,
  type CatalogChanged,
  type CatalogChangedHandler,
  type CatalogRegistrar,
} from './catalogs.js';

/** An id-less cloud-bridge frame as the daemon relays it (la ADR-048). */
interface RelayFrame {
  event?: string;
  data?: unknown;
}

/**
 * Handler for the daemon's `flags.updated` relay frame:
 * a full-map snapshot of the flags the daemon evaluated with ITS
 * credential — the same identity the renderer acquires — or `null` after a
 * sign-out clear. Consumers re-evaluate via `sdk.flags.evaluate`; the payload
 * is a snapshot, not a membership engine (flags ADR-002).
 */
export type FlagsUpdatedHandler = (flags: Record<string, boolean> | null) => void;

function closedRelayGate(): { opened: Promise<void>; open: () => void; isOpen: boolean } {
  let resolve: () => void = () => undefined;
  const gate = {
    opened: new Promise<void>((r) => {
      resolve = r;
    }),
    open: () => {
      gate.isOpen = true;
      resolve();
    },
    isOpen: false,
  };
  return gate;
}

const SUBSCRIBE_RETRY_BASE_MS = 1000;
const SUBSCRIBE_MAX_ATTEMPTS = 5;

/**
 * The notification half of `BridgeEventStream` over the daemon transport
 * (events ADR-012). Subscribe is re-attempted because the loopback socket can
 * open before the daemon's acked `bridge.identify` (sdk ADR-024) writes the
 * `active_connections` row `/v1/notifications/subscribe` requires; every
 * failure reaches `onError`.
 */
export class ShogunChopPushFrameStream {
  private readonly notificationHandlers = new Set<NotificationEventHandler>();
  private readonly errorHandlers = new Set<ErrorEventHandler>();
  private readonly feedHandlers = new Set<FeedEventHandler>();
  private readonly supportHandlers = new Set<SupportEventHandler>();
  private readonly flagsUpdatedHandlers = new Set<FlagsUpdatedHandler>();
  private readonly seenPushEventIds = new Set<string>();
  private readonly deviceId: string;
  private readonly registrar: NotificationRegistrar;
  private readonly cursorStore: NotificationCursorStore | null;
  private readonly onError: (err: unknown) => void;
  private readonly offRelay: () => void;
  private readonly offOpen: () => void;
  private readonly offClose: () => void;
  private readonly catalogHolders: CatalogHolderRegistry | null;
  private relayGate: { opened: Promise<void>; open: () => void; isOpen: boolean };
  private cursor: NotificationCursor | null = null;
  private readonly feedRegistrar: FeedRegistrar | null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private subscribeEpoch = 0;
  private feedRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private feedSubscribeEpoch = 0;
  private closed = false;

  constructor(
    events: ShogunChopSharedEvents,
    deviceId: string,
    registrar: NotificationRegistrar,
    onError: (err: unknown) => void,
    cursorStore?: NotificationCursorStore,
    catalogRegistrar?: CatalogRegistrar,
    feedRegistrar?: FeedRegistrar
  ) {
    this.deviceId = deviceId;
    this.registrar = registrar;
    this.feedRegistrar = feedRegistrar ?? null;
    this.cursorStore = cursorStore ?? null;
    this.cursor = this.cursorStore?.load() ?? null;
    this.onError = onError;
    this.offRelay = events.on((event) => this.acceptRelayFrame(event as RelayFrame));
    this.relayGate = closedRelayGate();
    this.catalogHolders = catalogRegistrar
      ? new CatalogHolderRegistry(
          () => this.deviceId,
          catalogRegistrar,
          () => this.relayGate.opened,
          this.onError
        )
      : null;
    // `onOpen` fires per loopback (re)connect, and immediately when already open.
    this.offOpen = events.onOpen(() => {
      this.relayGate.open();
      this.subscribe();
      this.subscribeFeed();
      this.catalogHolders?.resubscribeAll();
    });
    // A hold names its socket, so a hold issued between sockets waits for the next one (shogunchop ADR-ab462).
    this.offClose = events.onClose(() => {
      if (this.relayGate.isOpen) this.relayGate = closedRelayGate();
    });
  }

  registerCatalog(
    scopeType: string,
    scopeId: string,
    handler: CatalogChangedHandler
  ): { registered: Promise<void>; unsubscribe: () => void } {
    if (!this.catalogHolders) {
      throw new Error(
        'ShogunChopPushFrameStream.registerCatalog requires a CatalogRegistrar'
      );
    }
    return this.catalogHolders.register(scopeType, scopeId, handler);
  }

  /** Subscribe to notification push frames (live + replay). Returns an unsubscribe fn. */
  onNotificationEvent(handler: NotificationEventHandler): () => void {
    this.notificationHandlers.add(handler);
    return () => {
      this.notificationHandlers.delete(handler);
    };
  }

  /** Subscribe to error push frames (live + replay). Returns an unsubscribe fn. */
  onErrorEvent(handler: ErrorEventHandler): () => void {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  // Re-subscribed per relay open and per daemon `bridge:connected` (sdk ADR-024).
  registerFeed(handler: FeedEventHandler): () => void {
    if (!this.feedRegistrar) {
      throw new Error('ShogunChopPushFrameStream.registerFeed requires a FeedRegistrar');
    }
    this.feedHandlers.add(handler);
    if (this.feedHandlers.size === 1 && this.relayGate.isOpen) this.subscribeFeed();
    return () => {
      this.feedHandlers.delete(handler);
    };
  }

  onSupportEvent(handler: SupportEventHandler): () => void {
    this.supportHandlers.add(handler);
    return () => {
      this.supportHandlers.delete(handler);
    };
  }

  /** Subscribe to the daemon's `flags.updated` relay frames. Returns an unsubscribe fn. */
  onFlagsUpdated(handler: FlagsUpdatedHandler): () => void {
    this.flagsUpdatedHandlers.add(handler);
    return () => {
      this.flagsUpdatedHandlers.delete(handler);
    };
  }

  /**
   * Stop consuming the relay, leaving the device's subscription OPEN so the
   * backlog accrued while the app is closed replays on the next launch
   * (events ADR-012).
   */
  close(): void {
    if (this.catalogHolders) {
      // The loopback socket is shared, so this stream's close is not its end (app-shell ADR-026).
      const holders = this.catalogHolders;
      holders.releaseAll().catch(this.onError).finally(() => holders.close());
    }
    this.closed = true;
    this.clearRetry();
    this.clearFeedRetry();
    this.offOpen();
    this.offClose();
    this.relayGate.open();
    this.offRelay();
    this.notificationHandlers.clear();
    this.errorHandlers.clear();
    this.feedHandlers.clear();
    this.supportHandlers.clear();
    this.flagsUpdatedHandlers.clear();
  }

  private acceptRelayFrame(frame: RelayFrame): void {
    switch (frame.event) {
      case 'bridge:connected':
        this.subscribe();
        this.subscribeFeed();
        return;
      case 'notification.event':
        this.dispatchNotification(frame.data as NotificationEventFrame, false);
        return;
      case 'notification.events':
        for (const event of (frame.data as { events: NotificationEventFrame[] }).events) {
          this.dispatchNotification(event, true);
        }
        return;
      case 'error.event':
        this.dispatchError(frame.data as ErrorEventFrame, false);
        return;
      case 'error.events':
        for (const event of (frame.data as { events: ErrorEventFrame[] }).events) {
          this.dispatchError(event, true);
        }
        return;
      case 'catalog.changed':
        this.catalogHolders?.dispatch(frame.data as CatalogChanged);
        return;
      case 'feed.event':
        this.dispatchFeed(frame.data as FeedPost, false);
        return;
      case 'feed.events':
        for (const post of (frame.data as { posts: FeedPost[] }).posts) {
          this.dispatchFeed(post, true);
        }
        return;
      case 'support.event':
        this.dispatchSupport(frame.data as SupportEvent, false);
        return;
      case 'flags.updated': {
        const wire = (frame.data as { flags?: unknown } | null) ?? null;
        const map =
          wire && typeof wire === 'object' && wire.flags !== null && typeof wire.flags === 'object'
            ? (wire.flags as Record<string, boolean>)
            : null;
        for (const handler of this.flagsUpdatedHandlers) handler(map);
        return;
      }
      default:
        return;
    }
  }

  private dispatchNotification(frame: NotificationEventFrame, replay: boolean): void {
    if (!this.acceptPushEvent(frame.event_id, frame.created_at)) return;
    for (const handler of this.notificationHandlers) handler(frame, { replay });
  }

  private dispatchError(frame: ErrorEventFrame, replay: boolean): void {
    if (!this.acceptPushEvent(frame.event_id, frame.created_at)) return;
    for (const handler of this.errorHandlers) handler(frame, { replay });
  }

  private dispatchFeed(post: FeedPost, replay: boolean): void {
    for (const handler of this.feedHandlers) handler(post, { replay });
  }

  private dispatchSupport(event: SupportEvent, replay: boolean): void {
    for (const handler of this.supportHandlers) handler(event, { replay });
  }

  private acceptPushEvent(eventId: string, createdAt: string): boolean {
    if (!eventId || this.seenPushEventIds.has(eventId)) return false;
    this.seenPushEventIds.add(eventId);
    if (this.seenPushEventIds.size > SEEN_PUSH_EVENT_CAP) {
      const oldest = this.seenPushEventIds.values().next().value;
      if (oldest !== undefined) this.seenPushEventIds.delete(oldest);
    }
    if (
      createdAt &&
      (this.cursor === null ||
        comparePushKey(createdAt, eventId, this.cursor.created_at, this.cursor.id) > 0)
    ) {
      this.cursor = { created_at: createdAt, id: eventId };
      this.cursorStore?.save(this.cursor);
    }
    return true;
  }

  private subscribe(): void {
    if (this.closed) return;
    this.clearRetry();
    const epoch = ++this.subscribeEpoch;
    const attempt = (n: number): void => {
      this.registrar.subscribe(this.deviceId, this.cursor ?? undefined).catch((err) => {
        if (this.closed || epoch !== this.subscribeEpoch) return;
        this.onError(err);
        if (n >= SUBSCRIBE_MAX_ATTEMPTS) return;
        this.retryTimer = setTimeout(
          () => {
            if (this.closed || epoch !== this.subscribeEpoch) return;
            attempt(n + 1);
          },
          SUBSCRIBE_RETRY_BASE_MS * 2 ** (n - 1)
        );
      });
    };
    attempt(1);
  }

  private subscribeFeed(): void {
    const registrar = this.feedRegistrar;
    if (this.closed || !registrar || this.feedHandlers.size === 0) return;
    this.clearFeedRetry();
    const epoch = ++this.feedSubscribeEpoch;
    const attempt = (n: number): void => {
      registrar.subscribe(this.deviceId).catch((err) => {
        if (this.closed || epoch !== this.feedSubscribeEpoch) return;
        this.onError(err);
        if (n >= SUBSCRIBE_MAX_ATTEMPTS) return;
        this.feedRetryTimer = setTimeout(
          () => {
            if (this.closed || epoch !== this.feedSubscribeEpoch) return;
            attempt(n + 1);
          },
          SUBSCRIBE_RETRY_BASE_MS * 2 ** (n - 1)
        );
      });
    };
    attempt(1);
  }

  private clearRetry(): void {
    if (this.retryTimer === null) return;
    clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private clearFeedRetry(): void {
    if (this.feedRetryTimer === null) return;
    clearTimeout(this.feedRetryTimer);
    this.feedRetryTimer = null;
  }
}
